import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO_URL = "/assets/images/logo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { 
      name: "Learning Forums", 
      href: "/learning-forums",
      dropdown: [
        { name: "SPARC", href: "/learning-forums/sparc" },
        { name: "Acadelite", href: "/learning-forums/acadelite" },
        { name: "INSPIRE", href: "/learning-forums/inspire" },
        { name: "Edvantage", href: "/learning-forums/edvantage" },
      ]
    },
    { 
      name: "Other Services", 
      href: "/programs",
      dropdown: [
        { name: "FUTURE", href: "/programs/future" },
        { name: "Leadership Ascendancy", href: "/programs/leadership-ascendancy" },
        { name: "Global Ascend", href: "/programs/global-ascend" },
      ]
    },
    { name: "Gallery", href: "/gallery" },
    { name: "Advisory Board", href: "/advisory-board" },
    { name: "Our Partners", href: "/partners" },
    { name: "Newsletter", href: "/newsletter" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/95 backdrop-blur-md shadow-lg py-2" 
          : "bg-transparent py-4"
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="GSLC Logo" className="h-16 md:h-20 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden xl:flex items-center gap-5" ref={dropdownRef}>
          {navLinks.map((link) => (
            <div 
              key={link.name} 
              className="relative group"
              onMouseEnter={() => link.dropdown && setOpenDropdown(link.name)}
              onMouseLeave={() => link.dropdown && setOpenDropdown(null)}
            >
              {link.dropdown ? (
                <button
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-body text-sm tracking-wide relative transition-colors duration-300 flex items-center gap-1 py-2 ${
                    isActive(link.href) 
                      ? "text-red-600 font-semibold" 
                      : "text-slate-700 hover:text-blue-800"
                  }`}
                >
                  {link.name}
                  <ChevronDown size={14} className={`transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <Link
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-body text-sm tracking-wide relative group transition-colors duration-300 ${
                    isActive(link.href) 
                      ? "text-red-600 font-semibold" 
                      : "text-slate-700 hover:text-blue-800"
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 transition-all duration-300 ${
                    isActive(link.href) ? "w-full" : "w-0 group-hover:w-full"
                  }`} />
                </Link>
              )}

              {/* Dropdown Menu - Shows on Hover */}
              {link.dropdown && (
                <AnimatePresence>
                  {openDropdown === link.name && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 pt-2 w-56 z-50"
                    >
                      <div className="bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="block px-4 py-3 text-sm text-slate-700 hover:bg-gradient-to-r hover:from-yellow-50 hover:to-red-50 hover:text-red-600 transition-colors border-b border-slate-50 last:border-0"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="xl:hidden p-2 text-slate-800"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="xl:hidden bg-white shadow-xl mt-3 mx-4 rounded-2xl p-6 border border-slate-100 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.dropdown ? (
                    <>
                      <button
                        onClick={() => toggleDropdown(link.name)}
                        className={`w-full flex items-center justify-between py-2 font-body ${
                          isActive(link.href) 
                            ? "text-red-600 font-semibold" 
                            : "text-slate-700"
                        }`}
                      >
                        {link.name}
                        <ChevronDown size={16} className={`transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {openDropdown === link.name && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="pl-4 border-l-2 border-red-200 ml-2 overflow-hidden"
                          >
                            {link.dropdown.map((item) => (
                              <Link
                                key={item.name}
                                to={item.href}
                                className="block py-2 text-sm text-slate-600 hover:text-red-600"
                              >
                                {item.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={link.href}
                      className={`block py-2 font-body ${
                        isActive(link.href) 
                          ? "text-red-600 font-semibold" 
                          : "text-slate-700 hover:text-blue-800"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
